import {
  generateSrcSetAndSizes,
  type GenerateSrcSetAndSizesParams,
  type WidthsAtBreakpoint,
} from '@lib/services/image-service'
import { getImageProps, type ImageProps as NextImageProps } from 'next/image'
import { ensureProtocol } from '@lib/utilities/page-utilities'

const columnBasedImageSizes: Record<number, string> = {
  1: '100vw',
  2: '(max-width: 639px) 100vw, 50vw',
  3: '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw',
  4: '(max-width: 639px) 100vw, (max-width: 1023px) 50vw, (max-width: 1279px) 33vw, 25vw',
} as const

export type ImageBaseProps = {
  /** Responsive widths for each breakpoint (see WidthsAtBreakpoint for pixel ranges).
   *  Undefined values will cascade (e.g., if xs is undefined, sm will be used for xs).
   */
  breakpointWidths?: WidthsAtBreakpoint
  /** Aspect ratio (width / height) for the image */
  aspectRatio?: number
  /** Number of columns for default column-based sizing (usually from Contentful) */
  columnsMax?: number
} & NextImageProps

/**
 * ImageBase is a minimal wrapper around <img> logic for responsive images.
 *
 * Usage:
 * - Use `breakpointWidths` and `aspectRatio` for custom responsive images. This will generate a custom srcSet and sizes.
 *   - When using `breakpointWidths`, you do NOT need to provide `width` and `height`—they will be computed from the largest breakpoint and aspect ratio if omitted.
 * - Use `columnsMax` (with no `breakpointWidths`) for default column-based responsive sizing. This will use a standard sizes string based on the number of columns.
 *   - In this case, you should provide `width` and `height` as with a normal Next.js image.
 *
 * The component will always provide a `sizes` value, either custom or column-based, and will avoid passing conflicting width/height values to Next.js.
 */
const ImageBase = (props: ImageBaseProps) => {
  // Destructure width and height from props so we can compute or override them as needed.
  // This ensures that if breakpointWidths is provided, we can calculate width/height from breakpoints and aspect ratio,
  // and avoid passing conflicting values to getImageProps. Only the correct width/height will be used.
  const { breakpointWidths, aspectRatio = 16 / 9, columnsMax = 2, width, height, ...rest } = props
  // If using custom breakpoints, generate custom sizes and srcSet
  let customSizes: string | undefined
  let customSrcSet: string | undefined
  let computedWidth = width
  let computedHeight = height

  if (breakpointWidths) {
    const { sizes, srcSet } = generateSrcSetAndSizes({
      src: ensureProtocol(String(rest.src)),
      breakpointWidths,
      aspectRatio,
    } satisfies GenerateSrcSetAndSizesParams)
    customSizes = sizes
    customSrcSet = srcSet.join(', ')
    // If width/height not provided, use the largest breakpoint and aspect ratio
    if (!width || !height) {
      const maxWidth = Math.max(...Object.values(breakpointWidths))
      computedWidth = maxWidth
      computedHeight = Math.round(maxWidth / aspectRatio)
    }
  }

  // Always provide a sizes value, either custom or column-based
  const sizesValue = customSizes ?? rest.sizes ?? columnBasedImageSizes[columnsMax] ?? '100vw'

  // Use Next.js getImageProps to get the base props
  const { props: imageProps } = getImageProps({
    fill: !breakpointWidths,
    loading: 'lazy',
    sizes: sizesValue,
    width: computedWidth,
    height: computedHeight,
    ...rest,
    src: ensureProtocol(String(rest.src)),
  })

  // If custom srcSet is generated, override it
  if (customSrcSet) {
    imageProps.srcSet = customSrcSet
  }

  return (
    // Explicitly set alt to satisfy accessibility requirements and static analysis tools (e.g., SonarQube),
    // which does not detect alt when only spread via imageProps.
    // eslint-disable-next-line @next/next/no-img-element
    <img {...imageProps} alt={imageProps.alt} />
  )
}

export default ImageBase
